import { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import styles from './EventRoleTags.module.scss';

import TagsInput from 'Components/Common/TagsInput/TagsInput';
import { InputElement } from 'Components/Common/TagsInput/types';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import useGetUserPermission from 'hooks/useGetUserPermission';
import {
    EventRoleDto,
    useCreateEventRoleMutation,
    useDeleteEventRoleMutation,
    useGetEventRolesQuery,
} from 'api/eventRoleEndpoints';
import EventRoleEditModal from './EventRoleEditModal/EventRoleEditModal';
import useGetCurrentSubscription from 'Screens/Dashboard/Subscription/useGetCurrentSubscription';

const EventRoleTags = () => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const { data, isFetching, isError, refetch } = useGetEventRolesQuery(
        currentEntityId ?? '',
        { skip: !currentEntityId },
    );
    const [onAddRole, { isLoading: addLoading }] = useCreateEventRoleMutation();
    const [onRemoveRole, { isLoading: removeLoading }] = useDeleteEventRoleMutation();
    const { isAdmin, isModerator, isOwner } = useGetUserPermission();
    const {
        activeSubscription,
        isFetching: isFetchingSubscription,
        isPaidPlan,
    } = useGetCurrentSubscription();
    const hasPermission = isAdmin || isModerator || isOwner;
    const hasSubscriptionAccess = isPaidPlan && activeSubscription?.hasAccess !== false;
    const isAccessDisabled = !hasPermission || (!isFetchingSubscription && !hasSubscriptionAccess);

    const roles = useMemo(() => data ?? [], [data]);
    const [roleToEdit, setRoleToEdit] = useState<EventRoleDto | null>(null);

    const onAdd = useCallback(async (name: string) => {
        if (!currentEntityId) return;
        try {
            await onAddRole({ entityId: currentEntityId, data: { name } });
        } catch (e) {
            // handled in rtk query
        }
    }, [currentEntityId, onAddRole]);

    const onRemove = useCallback(async (role: InputElement | null) => {
        if (!role?.id || !currentEntityId) return;
        try {
            await onRemoveRole({ entityId: currentEntityId, id: role.id });
        } catch (e) {
            // handled in rtk query
        }
    }, [currentEntityId, onRemoveRole]);

    const onClick = useCallback((id?: string) => {
        const role = roles.find((r) => r.id === id) ?? null;
        setRoleToEdit(role);
    }, [roles]);

    const onModalClose = useCallback(() => {
        setRoleToEdit(null);
    }, []);

    if (isError) {
        return <ErrorActionState className={styles.error} onClick={refetch} />;
    }

    return (
        <>
            <TagsInput
                textInputId="event-role-tags-input"
                elements={roles}
                onAdd={onAdd}
                isDisabled={isAccessDisabled || isFetchingSubscription || isFetching || addLoading || removeLoading}
                disabledTooltip={isAccessDisabled ? intl.formatMessage(messages.disabledTooltip) : undefined}
                onRemove={onRemove}
                onClick={onClick}
                messages={messages}
            />
            <EventRoleEditModal
                open={!!roleToEdit}
                onClose={onModalClose}
                role={roleToEdit}
            />
        </>
    );
};

const messages = defineMessages({
    title: {
        id: 'eventRoles.tags.title',
        defaultMessage: 'Posługi liturgiczne',
    },
    titlePlaceholder: {
        id: 'eventRoles.tags.titlePlaceholder',
        defaultMessage: 'Wprowadź nazwę nowej posługi',
    },
    elementAlreadyExist: {
        id: 'eventRoles.tags.elementAlreadyExist',
        defaultMessage: 'Taka rola już istnieje. Dodaj inną.',
    },
    formName: {
        id: 'eventRoles.tags.formName',
        defaultMessage: 'Formularz ról wydarzeń',
    },
    disabledTooltip: {
        id: 'eventRoles.tags.disabledTooltip',
        defaultMessage: 'Nie masz odpowiednich uprawnień w tej grupie albo nie masz wykupionej odpowiedniej subskrypcji koniecznej do zarządzania posługami liturgicznymi.',
    },
});

export default EventRoleTags;
